/**
 * Handler Assíncrono para Edição de Matriz - V5 SEM GLOBAL SIGNAL
 * 
 * CORREÇÃO DEFINITIVA: Eliminar global signal completamente
 * - Signal é passado via op.context.signal (não global)
 * - Cada tentativa tem AbortController NOVO e EXCLUSIVO
 * - AbortError é tratado como fluxo normal (não exceção)
 * - Retry desabilitado para AbortError
 * - State machine: abort → idle (não error)
 * 
 * Fluxo:
 * 1. Abre editor imediatamente (UI responsiva)
 * 2. Dispara ensureDraft() COM AbortSignal via context
 * 3. Se timeout: aborta request (encerra fluxo)
 * 4. Cancelar: aborta request HTTP real + reset para idle
 * 
 * Feature Flag: FF_ASYNC_MATRIX_EDIT
 */

export interface AsyncEditConfig {
  featureFlagEnabled: boolean;
  timeoutMs: number;
  maxRetries: number;
}

export const defaultConfig: AsyncEditConfig = {
  featureFlagEnabled: false, // Default OFF para rollback fácil
  timeoutMs: 30000, // 30s (server timeout é 25s + 5s margem)
  maxRetries: 3,
};

// Contador global para attemptId único
let globalAttemptCounter = 0;

/**
 * Handler principal: Editar Matriz com State Machine + Signal Isolado por Tentativa
 * 
 * Uso:
 * const handler = createAsyncEditHandler(config);
 * await handler.openEditor(ensureDraftMutation, utils, callbacks);
 */
export function createAsyncEditHandler(config: AsyncEditConfig) {
  // Estado local do handler (NÃO compartilhado entre tentativas)
  let currentAttemptId: string | null = null;
  let currentAbortController: AbortController | null = null;
  let currentTimeoutId: NodeJS.Timeout | null = null;
  let isProcessing = false;

  /**
   * Limpar recursos da tentativa atual
   */
  function cleanupCurrentAttempt(attemptId: string) {
    console.log(`[AsyncMatrixEdit] ${attemptId} CLEANUP - limpando recursos`);
    
    // Limpar timeout
    if (currentTimeoutId) {
      clearTimeout(currentTimeoutId);
      currentTimeoutId = null;
    }
    
    // Abortar controller se ainda não foi abortado
    if (currentAbortController && !currentAbortController.signal.aborted) {
      console.log(`[AsyncMatrixEdit] ${attemptId} CLEANUP - abortando controller restante`);
      currentAbortController.abort();
    }
    
    currentAbortController = null;
    currentAttemptId = null;
    isProcessing = false;
    
    console.log(`[AsyncMatrixEdit] ${attemptId} CLEANUP - recursos limpos`);
  }

  return {
    /**
     * Abrir editor (abre UI imediatamente, carrega dados em background)
     */
    async openEditor(
      ensureDraftMutation: any,
      utils: any,
      callbacks: {
        onStateChange: (state: string, error?: string) => void;
        onSuccess: (result: any) => void;
        onError: (error: any) => void;
        onCancel: () => void;
      }
    ) {
      // Gerar attemptId ÚNICO para esta tentativa
      globalAttemptCounter++;
      const attemptId = `attempt_${globalAttemptCounter}_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      const controllerId = `ctrl_${globalAttemptCounter}`;
      
      console.log(`[AsyncMatrixEdit] ${attemptId} START - controllerId=${controllerId}`);
      
      // Se já existe tentativa em andamento, REJEITAR
      if (isProcessing) {
        console.warn(`[AsyncMatrixEdit] ${attemptId} REJEITADO - tentativa anterior em andamento (currentAttemptId=${currentAttemptId})`);
        callbacks.onStateChange('error', 'Operação já em andamento. Aguarde ou cancele.');
        return;
      }
      
      // Abortar controller anterior se ainda existe
      if (currentAbortController && !currentAbortController.signal.aborted) {
        console.warn(`[AsyncMatrixEdit] ${attemptId} AVISO - controller anterior ainda ativo, abortando`);
        currentAbortController.abort();
      }
      
      // Criar AbortController NOVO e EXCLUSIVO para esta tentativa
      const abortController = new AbortController();
      const signal = abortController.signal;
      
      console.log(`[AsyncMatrixEdit] ${attemptId} CONTROLLER CRIADO - controllerId=${controllerId}, signal.aborted=${signal.aborted}`);
      
      if (signal.aborted) {
        console.error(`[AsyncMatrixEdit] ${attemptId} ERRO CRÍTICO - signal já abortado no momento da criação!`);
        callbacks.onStateChange('error', 'Erro interno: controller inválido');
        return;
      }
      
      // Marcar como processando e guardar referências
      isProcessing = true;
      currentAttemptId = attemptId;
      currentAbortController = abortController;

      // Estado: opening (UI abre imediatamente)
      callbacks.onStateChange('opening');
      console.log(`[AsyncMatrixEdit] ${attemptId} STATE -> opening`);

      try {
        // Estado: preparingDraft (carregando dados)
        callbacks.onStateChange('preparingDraft');
        console.log(`[AsyncMatrixEdit] ${attemptId} STATE -> preparingDraft`);

        // Log ANTES do fetch/tRPC call
        console.log(`[AsyncMatrixEdit] ${attemptId} FETCH START - signal.aborted=${signal.aborted}`);

        // Validar que signal NÃO está abortado
        if (signal.aborted) {
          throw new Error(`[AsyncMatrixEdit] ${attemptId} Signal abortado antes do fetch`);
        }

        // Criar promise da mutação COM signal via context
        // IMPORTANTE: Signal é passado via context, não global
        // O customSignalLink no main.tsx extrai signal de op.context
        const mutationPromise = ensureDraftMutation.mutateAsync(
          undefined, // input (não tem input)
          {
            context: {
              signal, // ← Passa signal para tRPC via context
            },
          }
        );

        // Timeout com ABORT OBRIGATÓRIO
        const timeoutPromise = new Promise<never>((_, reject) => {
          currentTimeoutId = setTimeout(() => {
            console.warn(`[AsyncMatrixEdit] ${attemptId} TIMEOUT após ${config.timeoutMs}ms - ABORTANDO request`);
            
            // Abortar o controller DESTA tentativa
            if (abortController && !signal.aborted) {
              abortController.abort();
              console.log(`[AsyncMatrixEdit] ${attemptId} ABORT chamado via timeout`);
            }
            
            reject(new Error(`[AsyncMatrixEdit] Timeout após ${config.timeoutMs}ms`));
          }, config.timeoutMs);
        });

        // Race entre mutação e timeout
        console.log(`[AsyncMatrixEdit] ${attemptId} RACE START`);
        const result = await Promise.race([mutationPromise, timeoutPromise]);

        // Sucesso - limpar timeout
        if (currentTimeoutId) {
          clearTimeout(currentTimeoutId);
          currentTimeoutId = null;
        }

        // Log no RESOLVE
        console.log(`[AsyncMatrixEdit] ${attemptId} RESOLVE - sucesso`);

        // Estado: ready (sucesso)
        callbacks.onStateChange('ready');
        console.log(`[AsyncMatrixEdit] ${attemptId} STATE -> ready`);
        callbacks.onSuccess(result);

      } catch (error: any) {
        // Log no REJECT
        console.log(`[AsyncMatrixEdit] ${attemptId} REJECT - erro: ${error?.message}`);

        // Diferenciar erro de abort/cancel vs timeout vs erro real
        const isCanceled = 
          error?.name === 'AbortError' ||
          error?.message?.includes('Canceled') ||
          error?.message?.includes('cancelada') ||
          error?.message?.includes('aborted') ||
          error?.message?.includes('signal is aborted') ||
          error?.message?.includes('Request aborted');
        
        const isTimeout = error?.message?.includes('Timeout');

        if (isCanceled) {
          // Cancelamento: fluxo normal, não é exceção
          console.log(`[AsyncMatrixEdit] ${attemptId} CANCELED - tratando como fluxo normal`);
          callbacks.onStateChange('idle');
          console.log(`[AsyncMatrixEdit] ${attemptId} STATE -> idle (canceled)`);
          callbacks.onCancel();
        } else if (isTimeout) {
          // Timeout: request foi abortada
          console.warn(`[AsyncMatrixEdit] ${attemptId} TIMEOUT - request abortada`);
          callbacks.onStateChange('error', 'Timeout ao preparar edição. Tente novamente.');
          console.log(`[AsyncMatrixEdit] ${attemptId} STATE -> error (timeout)`);
          callbacks.onError(error);
        } else {
          // Erro real
          console.error(`[AsyncMatrixEdit] ${attemptId} ERRO REAL:`, error);
          callbacks.onStateChange('error', error?.message || 'Erro desconhecido');
          console.log(`[AsyncMatrixEdit] ${attemptId} STATE -> error (real)`);
          callbacks.onError(error);
        }

      } finally {
        // Log no FINALLY com estado final
        console.log(`[AsyncMatrixEdit] ${attemptId} FINALLY - limpando recursos`);
        
        // Limpar recursos DESTA tentativa
        cleanupCurrentAttempt(attemptId);
        
        console.log(`[AsyncMatrixEdit] ${attemptId} END - isProcessing=${isProcessing}, currentAttemptId=${currentAttemptId}`);
      }
    },

    /**
     * Cancelar edição (abortar request HTTP real + reset)
     */
    cancel() {
      const attemptId = currentAttemptId || 'unknown';
      console.log(`[AsyncMatrixEdit] ${attemptId} CANCEL CHAMADO`);

      // Abortar controller se existe
      if (currentAbortController && !currentAbortController.signal.aborted) {
        console.log(`[AsyncMatrixEdit] ${attemptId} CANCEL - abortando controller`);
        currentAbortController.abort();
      }

      // Limpar timeout se existe
      if (currentTimeoutId) {
        console.log(`[AsyncMatrixEdit] ${attemptId} CANCEL - limpando timeout`);
        clearTimeout(currentTimeoutId);
        currentTimeoutId = null;
      }

      // Reset estado
      currentAbortController = null;
      currentAttemptId = null;
      isProcessing = false;

      console.log(`[AsyncMatrixEdit] ${attemptId} CANCEL - recursos limpos`);
    },

    /**
     * Retry (tentar novamente após erro)
     */
    retry(
      ensureDraftMutation: any,
      utils: any,
      callbacks: {
        onStateChange: (state: string, error?: string) => void;
        onSuccess: (result: any) => void;
        onError: (error: any) => void;
        onCancel: () => void;
      }
    ) {
      const attemptId = currentAttemptId || 'unknown';
      console.log(`[AsyncMatrixEdit] ${attemptId} RETRY CHAMADO`);

      // Limpar recursos da tentativa anterior
      this.cancel();

      // Tentar novamente
      return this.openEditor(ensureDraftMutation, utils, callbacks);
    },

    /**
     * Estado atual (para debug)
     */
    getDebugState() {
      return {
        currentAttemptId,
        isProcessing,
        hasAbortController: !!currentAbortController,
        signalAborted: currentAbortController?.signal.aborted ?? null,
        hasTimeout: !!currentTimeoutId,
      };
    },
  };
}
